import { createContext, useContext, useState, useCallback } from 'react';
import { authAPI } from '../api';

const AuthContext = createContext(null);
const AUTH_STORAGE_KEYS = ['access_token', 'refresh_token', 'user'];

const clearAuthStorage = () => {
  AUTH_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
};

const normalizeRole = (role) => (typeof role === 'string' ? role.trim().toUpperCase() : '');

const getStoredUser = () => {
  const storedUser = localStorage.getItem('user');
  const accessToken = localStorage.getItem('access_token');
  const refreshToken = localStorage.getItem('refresh_token');

  if (!storedUser || !accessToken || !refreshToken) {
    return null;
  }

  try {
    const parsed = JSON.parse(storedUser);
    if (parsed && typeof parsed === 'object') {
      return { ...parsed, role: normalizeRole(parsed.role) };
    }
    return null;
  } catch (_) {
    clearAuthStorage();
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  // Login response gives us: access, refresh, role, name, email, phone_number
  // We store everything in localStorage and mirror in state
  const [user, setUser] = useState(getStoredUser);

  const login = useCallback(async (email, password) => {
    const { data } = await authAPI.login(email, password);
    // data: { access, refresh, role, name, email, phone_number, message }
    localStorage.setItem('access_token', data.access);
    localStorage.setItem('refresh_token', data.refresh);
    const userInfo = {
      id: data.id ?? data.user_id ?? data.uid ?? null,
      name: data.name,
      email: data.email,
      role: normalizeRole(data.role),           // 'CA' | 'AM' | 'CLNT'
      phone_number: data.phone_number,
      profile_photo: data.profile_photo || '',
      is_tour_completed: Boolean(data.is_tour_completed),
    };
    localStorage.setItem('user', JSON.stringify(userInfo));
    setUser(userInfo);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try { await authAPI.logout(); } catch (_) {}
    clearAuthStorage();
    setUser(null);
  }, []);

  const isCA   = user?.role === 'CA';
  const isAM   = user?.role === 'AM';
  const isCLNT = user?.role === 'CLNT';

  const updateUser = useCallback((updates) => {
    setUser((prev) => {
      const next = { ...prev, ...updates };
      localStorage.setItem('user', JSON.stringify(next));
      return next;
    });
  }, []);

  return (
    <AuthContext.Provider value={{ user, login, logout, updateUser, isCA, isAM, isCLNT }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
